import React, { useEffect, useMemo, useState } from 'react';
import {
    exportSimulationUsersExcel,
    getOpenPageDetailsByUsername,
    getSimulationStatusByUsername,
    getSimulationUserDetails
} from '../../api/ifec-verify-services/IfecVerifyService';
import 'assets/scss/page/admin-page-style.scss';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 10;
const DETAIL_PAGE_SIZE = 10;

const getVisiblePages = (activePage, totalPages) => {
    if (totalPages <= 0) return [];
    const maxVisible = 5;
    let start = Math.max(0, activePage - 2);
    let end = Math.min(totalPages, start + maxVisible);
    if (end - start < maxVisible) {
        start = Math.max(0, end - maxVisible);
    }
    const numbers = [];
    for (let i = start; i < end; i += 1) {
        numbers.push(i);
    }
    return numbers;
};

const parsePageResponse = (raw) => {
    if (Array.isArray(raw?.content)) {
        return {
            rows: raw.content,
            totalPages: raw.totalPages || 0,
            totalElements: raw.totalElements || 0
        };
    }
    if (Array.isArray(raw?.data?.content)) {
        return {
            rows: raw.data.content,
            totalPages: raw.data.totalPages || 0,
            totalElements: raw.data.totalElements || 0
        };
    }
    if (Array.isArray(raw)) {
        return {
            rows: raw,
            totalPages: raw.length > 0 ? 1 : 0,
            totalElements: raw.length
        };
    }
    if (raw && typeof raw === 'object' && Object.keys(raw).length > 0) {
        return {
            rows: [raw],
            totalPages: 1,
            totalElements: 1
        };
    }
    return { rows: [], totalPages: 0, totalElements: 0 };
};

const AdminPage = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [exportLoading, setExportLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchDraft, setSearchDraft] = useState('');
    const [searchText, setSearchText] = useState('');

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailUsername, setDetailUsername] = useState('');
    const [detailUserInfo, setDetailUserInfo] = useState({ fullname: '', email: '', username: '' });
    const [detailRows, setDetailRows] = useState([]);
    const [detailColumns, setDetailColumns] = useState([]);
    const [detailPage, setDetailPage] = useState(0);
    const [detailTotalPages, setDetailTotalPages] = useState(0);
    const [detailTotalElements, setDetailTotalElements] = useState(0);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');

    const [simuDetailOpen, setSimuDetailOpen] = useState(false);
    const [simuDetailUsername, setSimuDetailUsername] = useState('');
    const [simuDetailUserInfo, setSimuDetailUserInfo] = useState({ fullname: '', email: '', username: '' });
    const [simuDetailRows, setSimuDetailRows] = useState([]);
    const [simuDetailColumns, setSimuDetailColumns] = useState([]);
    const [simuDetailPage, setSimuDetailPage] = useState(0);
    const [simuDetailTotalPages, setSimuDetailTotalPages] = useState(0);
    const [simuDetailTotalElements, setSimuDetailTotalElements] = useState(0);
    const [simuDetailLoading, setSimuDetailLoading] = useState(false);
    const [simuDetailError, setSimuDetailError] = useState('');

    const fetchData = () => {
        setLoading(true);
        setError('');
        getSimulationUserDetails(page, PAGE_SIZE, searchText)
            .then((res) => {
                if (res.status === 403) {
                    navigate('/login');
                }
                if (res?.error) {
                    setError('Veriler alınırken bir hata oluştu. Lütfen tekrar deneyin.');
                    setRows([]);
                    setTotalPages(0);
                    setTotalElements(0);
                    return;
                }

                const parsed = parsePageResponse(res?.data);
                setRows(parsed.rows);
                setTotalPages(parsed.totalPages);
                setTotalElements(parsed.totalElements);
            })
            .catch(() => {
                setError('Veriler alınırken bir hata oluştu. Lütfen tekrar deneyin.');
                setRows([]);
                setTotalPages(0);
                setTotalElements(0);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const fetchDetailData = () => {
        if (!detailOpen || !detailUsername) return;
        setDetailLoading(true);
        setDetailError('');
        getOpenPageDetailsByUsername(detailUsername, detailPage, DETAIL_PAGE_SIZE)
            .then((res) => {
                if (res.status === 403) {
                    navigate('/login');
                }
                if (res?.error) {
                    setDetailError('Detay verileri alınırken bir hata oluştu.');
                    setDetailRows([]);
                    setDetailColumns([]);
                    setDetailTotalPages(0);
                    setDetailTotalElements(0);
                    return;
                }

                const parsed = parsePageResponse(res?.data);
                setDetailRows(parsed.rows);
                setDetailTotalPages(parsed.totalPages);
                setDetailTotalElements(parsed.totalElements);

                const columns = parsed.rows.length > 0
                    ? Object.keys(parsed.rows[0]).filter((column) => column.toLowerCase() !== 'username')
                    : [];
                setDetailColumns(columns);
            })
            .catch(() => {
                setDetailError('Detay verileri alınırken bir hata oluştu.');
                setDetailRows([]);
                setDetailColumns([]);
                setDetailTotalPages(0);
                setDetailTotalElements(0);
            })
            .finally(() => {
                setDetailLoading(false);
            });
    };

    const fetchSimuDetailData = () => {
        if (!simuDetailOpen || !simuDetailUsername) return;
        setSimuDetailLoading(true);
        setSimuDetailError('');
        getSimulationStatusByUsername(simuDetailUsername, simuDetailPage, DETAIL_PAGE_SIZE)
            .then((res) => {
                if (res.status === 403) {
                    navigate('/login');
                }
                if (res?.error) {
                    setSimuDetailError('Simülasyon detay verileri alınırken bir hata oluştu.');
                    setSimuDetailRows([]);
                    setSimuDetailColumns([]);
                    setSimuDetailTotalPages(0);
                    setSimuDetailTotalElements(0);
                    return;
                }

                const parsed = parsePageResponse(res?.data);
                setSimuDetailRows(parsed.rows);
                setSimuDetailTotalPages(parsed.totalPages);
                setSimuDetailTotalElements(parsed.totalElements);

                const columns = parsed.rows.length > 0 ? Object.keys(parsed.rows[0]) : [];
                setSimuDetailColumns(columns);
            })
            .catch(() => {
                setSimuDetailError('Simülasyon detay verileri alınırken bir hata oluştu.');
                setSimuDetailRows([]);
                setSimuDetailColumns([]);
                setSimuDetailTotalPages(0);
                setSimuDetailTotalElements(0);
            })
            .finally(() => {
                setSimuDetailLoading(false);
            });
    };

    useEffect(() => {
        fetchData();
    }, [page, searchText]);

    useEffect(() => {
        fetchDetailData();
    }, [detailOpen, detailUsername, detailPage]);

    useEffect(() => {
        fetchSimuDetailData();
    }, [simuDetailOpen, simuDetailUsername, simuDetailPage]);

    const formatDate = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '-';
        return date.toLocaleString('tr-TR');
    };

    const formatDateAsUtcPlus3 = (value) => {
        if (!value) return '-';
        const utcDate = new Date(value);
        if (Number.isNaN(utcDate.getTime())) return '-';
        const plus3Date = new Date(utcDate.getTime() + (3 * 60 * 60 * 1000));
        return plus3Date.toLocaleString('tr-TR', { timeZone: 'UTC' });
    };

    const formatCellValue = (key, value) => {
        if (value === null || value === undefined || value === '') return '-';
        if (typeof value === 'object') return JSON.stringify(value);
        if (typeof value === 'string' && key.toLowerCase().includes('date')) {
            return formatDate(value);
        }
        return value.toString();
    };

    const handleSearch = () => {
        setPage(0);
        setSearchText(searchDraft.trim());
    };

    const clearSearch = () => {
        setSearchDraft('');
        setSearchText('');
        setPage(0);
    };

    const handleExportExcel = () => {
        setExportLoading(true);
        exportSimulationUsersExcel()
            .then((response) => {
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const now = new Date();
                const pad = (val) => val.toString().padStart(2, '0');
                const formattedDate =
                    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_` +
                    `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `KullaniciListesi-${formattedDate}.xlsx`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);
            })
            .catch(() => {
                setError('Excel indirilirken bir hata oluştu. Lütfen tekrar deneyin.');
            })
            .finally(() => {
                setExportLoading(false);
            });
    };

    const openDetailModal = (userRow) => {
        const username = userRow?.username;
        if (!username) return;
        setDetailUsername(username);
        setDetailUserInfo({
            fullname: userRow?.fullname || '-',
            email: userRow?.email || '-',
            username: userRow?.username || '-'
        });
        setDetailPage(0);
        setDetailRows([]);
        setDetailColumns([]);
        setDetailTotalPages(0);
        setDetailTotalElements(0);
        setDetailError('');
        setDetailOpen(true);
    };

    const closeDetailModal = () => {
        setDetailOpen(false);
    };

    const openSimuDetailModal = (userRow) => {
        const username = userRow?.username;
        if (!username) return;
        setSimuDetailUsername(username);
        setSimuDetailUserInfo({
            fullname: userRow?.fullname || '-',
            email: userRow?.email || '-',
            username: userRow?.username || '-'
        });
        setSimuDetailPage(0);
        setSimuDetailRows([]);
        setSimuDetailColumns([]);
        setSimuDetailTotalPages(0);
        setSimuDetailTotalElements(0);
        setSimuDetailError('');
        setSimuDetailOpen(true);
    };

    const closeSimuDetailModal = () => {
        setSimuDetailOpen(false);
    };

    const visiblePageNumbers = useMemo(() => getVisiblePages(page, totalPages), [page, totalPages]);
    const visibleDetailPageNumbers = useMemo(
        () => getVisiblePages(detailPage, detailTotalPages),
        [detailPage, detailTotalPages]
    );
    const visibleSimuDetailPageNumbers = useMemo(
        () => getVisiblePages(simuDetailPage, simuDetailTotalPages),
        [simuDetailPage, simuDetailTotalPages]
    );
    const simuStartClientIdKey = useMemo(() => {
        return simuDetailColumns.find((column) => column.toLowerCase() === 'startclientid') ||
            simuDetailColumns.find((column) => column.toLowerCase().includes('startclientid')) ||
            '';
    }, [simuDetailColumns]);
    const simuEndClientIdKey = useMemo(() => {
        return simuDetailColumns.find((column) => column.toLowerCase() === 'endclientid') ||
            simuDetailColumns.find((column) => column.toLowerCase().includes('endclientid')) ||
            '';
    }, [simuDetailColumns]);
    const simuEndDateKey = useMemo(() => {
        return simuDetailColumns.find((column) => column.toLowerCase() === 'enddate') ||
            simuDetailColumns.find((column) => column.toLowerCase().includes('enddate')) ||
            '';
    }, [simuDetailColumns]);
    const simuStartDateKey = useMemo(() => {
        return simuDetailColumns.find((column) => column.toLowerCase() === 'startdate') ||
            simuDetailColumns.find((column) => column.toLowerCase().includes('startdate')) ||
            '';
    }, [simuDetailColumns]);
    const simuUsernameKey = useMemo(() => {
        return simuDetailColumns.find((column) => column.toLowerCase() === 'username') ||
            simuDetailColumns.find((column) => column.toLowerCase().includes('username')) ||
            '';
    }, [simuDetailColumns]);
    const orderedSimuDetailColumns = useMemo(() => {
        if (!simuStartClientIdKey) return simuDetailColumns;
        const others = simuDetailColumns.filter((column) =>
            column !== simuStartClientIdKey &&
            column !== simuStartDateKey &&
            column !== simuEndDateKey
        );
        const dateColumns = [];
        if (simuStartDateKey) dateColumns.push(simuStartDateKey);
        if (simuEndDateKey) dateColumns.push(simuEndDateKey);

        return [simuStartClientIdKey, ...dateColumns, ...others];
    }, [simuDetailColumns, simuStartClientIdKey, simuStartDateKey, simuEndDateKey]);
    const simuDetailRowsProcessed = useMemo(() => {
        if (!simuStartClientIdKey) return simuDetailRows;

        let sessionNo = 1;
        let lastClientId = null;

        return simuDetailRows.map((row, index) => {
            const rawClientId = row[simuStartClientIdKey];
            const normalizedClientId = rawClientId ? rawClientId.toString().trim().toLowerCase() : '';

            if (index === 0) {
                sessionNo = 1;
                lastClientId = normalizedClientId;
            } else if (normalizedClientId !== lastClientId) {
                sessionNo += 1;
                lastClientId = normalizedClientId;
            }

            return {
                ...row,
                sessionLabel: `${sessionNo}. Oturum`
            };
        });
    }, [simuDetailRows, simuStartClientIdKey]);
    const detailOpenDateKey = useMemo(() => {
        return detailColumns.find((column) => column.toLowerCase() === 'opendate') ||
            detailColumns.find((column) => column.toLowerCase().includes('opendate')) ||
            '';
    }, [detailColumns]);
    const detailClientIdKey = useMemo(() => {
        return detailColumns.find((column) => column.toLowerCase() === 'clientid') ||
            detailColumns.find((column) => column.toLowerCase().includes('clientid')) ||
            '';
    }, [detailColumns]);
    const detailPageKey = useMemo(() => {
        return detailColumns.find((column) => column.toLowerCase() === 'page') ||
            detailColumns.find((column) => column.toLowerCase() === 'pagename') ||
            detailColumns.find((column) => column.toLowerCase() === 'openpage') ||
            detailColumns.find((column) => column.toLowerCase().includes('page')) ||
            '';
    }, [detailColumns]);

    const detailRowsProcessed = useMemo(() => {
        if (!detailOpenDateKey && !detailClientIdKey) return detailRows;

        const formatDuration = (ms) => {
            if (!ms || ms <= 0) return '-';
            const totalSeconds = Math.floor(ms / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            const parts = [];
            if (hours > 0) parts.push(`${hours} sa`);
            if (minutes > 0) parts.push(`${minutes} dk`);
            if (seconds > 0 || parts.length === 0) parts.push(`${seconds} sn`);
            return parts.join(' ');
        };

        let sessionNo = 1;
        let lastClientId = null;
        const colorMap = new Map();
        let colorCursor = 0;

        const colors = [
            { bg: '#e8f2ff', border: '#8cb7f0', text: '#1f4f91' },
            { bg: '#e8f8ef', border: '#8bd2a6', text: '#1c7a45' },
            { bg: '#fff2e8', border: '#f4b487', text: '#9c4f14' },
            { bg: '#f2ecff', border: '#b69af2', text: '#5e37a1' },
            { bg: '#ffecef', border: '#f4a0b2', text: '#a32643' }
        ];

        return detailRows.map((row, index) => {
            const newRow = { ...row, spentTimeLabel: '-', sessionLabel: '-', sessionColor: null };

            if (detailOpenDateKey) {
                if (index < detailRows.length - 1) {
                    const current = new Date(row[detailOpenDateKey]).getTime();
                    const next = new Date(detailRows[index + 1][detailOpenDateKey]).getTime();
                    if (!Number.isNaN(current) && !Number.isNaN(next)) {
                        newRow.spentTimeLabel = formatDuration(Math.abs(current - next));
                    }
                }
            }

            if (detailClientIdKey) {
                const rawClientId = row[detailClientIdKey];
                const normalizedClientId = rawClientId ? rawClientId.toString().trim().toLowerCase() : '';
                if (index === 0) {
                    sessionNo = 1;
                    lastClientId = normalizedClientId;
                } else if (normalizedClientId !== lastClientId) {
                    sessionNo += 1;
                    lastClientId = normalizedClientId;
                }

                if (!colorMap.has(sessionNo)) {
                    colorMap.set(sessionNo, colors[colorCursor % colors.length]);
                    colorCursor += 1;
                }

                newRow.sessionLabel = `${sessionNo}. Oturum`;
                newRow.sessionColor = colorMap.get(sessionNo);
            }

            return newRow;
        });
    }, [detailRows, detailOpenDateKey, detailClientIdKey]);

    return (
        <div className="admin-layout">
            {loading && (
                <div className="admin-loading-overlay">
                    <div className="admin-loading-spinner" />
                    <p>Yükleniyor...</p>
                </div>
            )}

            <header className="admin-header">
                <h1><img alt="logo" width={34} src="/favicon.ico" /> Onboard Portal Simülasyon - Admin</h1>
                <p>Kullanıcı kayıtlarını inceleyin, ad soyad veya e-posta ile filtreleyin.</p>
            </header>

            <main className="admin-content">
                <section className="admin-panel">
                    <div className="admin-toolbar">
                        <div className="admin-search-block">
                            <label htmlFor="admin-search">Arama</label>
                            <input
                                id="admin-search"
                                type="text"
                                value={searchDraft}
                                placeholder="Ad soyad veya e-posta adresi giriniz"
                                onChange={(e) => setSearchDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSearch();
                                }}
                            />
                        </div>

                        <div className="admin-toolbar-buttons">
                            <button className="admin-btn admin-btn-primary" onClick={() => handleSearch()}>
                                Ara
                            </button>
                            <button className="admin-btn admin-btn-ghost" onClick={() => clearSearch()}>
                                Temizle
                            </button>
                            <button
                                className="admin-btn admin-btn-export"
                                onClick={() => handleExportExcel()}
                                disabled={exportLoading}
                            >
                                {exportLoading ? (
                                    <span className="admin-btn-loading">
                                        <span className="admin-btn-spinner" />
                                        Export...
                                    </span>
                                ) : 'Export'}
                            </button>
                        </div>
                    </div>

                    <div className="admin-meta">
                        <span>Toplam kayıt: {totalElements}</span>
                        <span>|</span>
                        <span>Aktif sayfa: {totalPages === 0 ? 0 : page + 1}</span>
                    </div>

                    <div className="admin-table-wrapper">
                        <table className="admin-table">
                            <thead>
                            <tr>
                                <th>Ad Soyad</th>
                                <th>E-posta</th>
                                <th>Kullanıcı Adı</th>
                                <th>Sisteme İlk Giriş Tarihi</th>
                                <th>En Son Giriş Tarihi</th>
                                <th>Bilgilendirme</th>
                                <th>
                                    <span className="admin-header-cell-title">Simülasyon Detayı</span>
                                    <span className="admin-header-cell-subtitle">Tıklayarak detaylara ulaşabilirsiniz</span>
                                </th>
                                <th className="admin-col-detail">Detay</th>
                            </tr>
                            </thead>
                            <tbody>
                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="admin-empty">
                                        Kayıt bulunamadı.
                                    </td>
                                </tr>
                            )}

                            {rows.map((row, index) => (
                                <tr key={`${row.username || 'user'}-${index}`}>
                                    {(() => {
                                        const completed = row.hasCompletedSimulation === true || row.hasCompletedSimulation === 'true';
                                        const accepted = !!row.acceptRightsDate;
                                        const hasStarted = !!row.createDate;
                                        const rightsState = accepted ? 'ok' : (hasStarted ? 'no' : 'pending');
                                        const rightsLabel = accepted
                                            ? 'Bilgilendirme Metni Kabul Edildi'
                                            : (hasStarted ? 'Bilgilendirme Metni Kabul Edilmedi' : 'Sisteme Giriş Yapmadı');
                                        return (
                                            <>
                                    <td>{row.fullname || '-'}</td>
                                    <td>{row.email || '-'}</td>
                                    <td>{row.username || '-'}</td>
                                    <td>{formatDate(row.createDate)}</td>
                                    <td>{formatDate(row.updateDate)}</td>
                                    <td>
                                            <span
                                                className="admin-tooltip"
                                                data-tooltip={
                                                    row.acceptRightsDate
                                                        ? `Bilgilendirme kabul tarihi: ${formatDate(row.acceptRightsDate)}`
                                                        : 'Bilgilendirme kabul tarihi bulunamadı'
                                                }
                                            >
                                                <button
                                                    type="button"
                                                    className={`admin-rights-pill ${rightsState}`}
                                                >
                                                    <span className={`admin-rights-icon ${rightsState}`} aria-hidden="true">
                                                        {accepted ? '✓' : (hasStarted ? '✕' : '•')}
                                                    </span>
                                                    {rightsLabel}
                                                </button>
                                            </span>
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className={`admin-detail-btn admin-sim-detail-btn ${
                                                completed ? 'completed' : 'failed'
                                            }`}
                                            onClick={() => openSimuDetailModal(row)}
                                            disabled={!row.username}
                                        >
                                            <span
                                                className={`admin-sim-icon ${completed ? 'ok' : 'fail'}`}
                                            >
                                                {completed ? (
                                                    <svg viewBox="0 0 16 16" aria-hidden="true">
                                                        <path d="M3.5 8.5 6.8 11.7 12.5 5.8" />
                                                    </svg>
                                                ) : (
                                                    <svg viewBox="0 0 16 16" aria-hidden="true">
                                                        <path d="M4.2 4.2 11.8 11.8" />
                                                        <path d="M11.8 4.2 4.2 11.8" />
                                                    </svg>
                                                )}
                                            </span>
                                            Simülasyon
                                        </button>
                                    </td>
                                    <td className="admin-col-detail">
                                        <button
                                            type="button"
                                            className="admin-detail-btn"
                                            onClick={() => openDetailModal(row)}
                                            disabled={!row.username}
                                        >
                                            Detay
                                        </button>
                                    </td>
                                            </>
                                        );
                                    })()}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {loading && <p className="admin-info">Veriler yükleniyor...</p>}
                    {error && <p className="admin-error">{error}</p>}

                    <div className="admin-pagination">
                        <button
                            className="admin-page-btn"
                            onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                            disabled={page === 0 || loading}
                        >
                            Geri
                        </button>

                        <div className="admin-page-numbers">
                            {visiblePageNumbers.map((pageNumber) => (
                                <button
                                    key={pageNumber}
                                    className={`admin-page-btn ${pageNumber === page ? 'active' : ''}`}
                                    onClick={() => setPage(pageNumber)}
                                    disabled={loading}
                                >
                                    {pageNumber + 1}
                                </button>
                            ))}
                        </div>

                        <button
                            className="admin-page-btn"
                            onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                            disabled={loading || totalPages === 0 || page >= totalPages - 1}
                        >
                            İleri
                        </button>
                    </div>
                </section>
            </main>

            <footer className="admin-footer">
                <p>Onboard Portal Simülasyon</p>
                <span>Admin Panel</span>
            </footer>

            {detailOpen && (
                <div className="admin-modal-backdrop" onClick={() => closeDetailModal()}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <div>
                                <h3>Detay Kayıtları</h3>
                                <p>
                                    Kullanıcı: {detailUserInfo.fullname} | {detailUserInfo.email}
                                </p>
                            </div>
                            <button type="button" className="admin-modal-close" onClick={() => closeDetailModal()}>
                                Kapat
                            </button>
                        </div>

                        <div className="admin-meta admin-modal-meta">
                            <span>Toplam kayıt: {detailTotalElements}</span>
                            <span>|</span>
                            <span>Aktif sayfa: {detailTotalPages === 0 ? 0 : detailPage + 1}</span>
                        </div>

                        <div className="admin-modal-content-area">
                            {detailLoading && (
                                <div className="admin-modal-loading-overlay">
                                    <div className="admin-loading-spinner" />
                                    <p>Detay yükleniyor...</p>
                                </div>
                            )}

                            <div className="admin-table-wrapper admin-modal-table-wrapper">
                                <table className="admin-table admin-modal-table admin-simu-table">
                                    <thead>
                                    <tr>
                                        <th>Oturum</th>
                                        <th>Sayfaya Giriş Tarihi</th>
                                        <th>Sayfada Geçirdiği Zaman</th>
                                        <th>Bulunduğu Sayfa</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {!detailLoading && detailRowsProcessed.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="admin-empty">
                                                Detay kaydı bulunamadı.
                                            </td>
                                        </tr>
                                    )}

                                    {detailRowsProcessed.map((row, rowIndex) => (
                                        <tr key={`detail-${rowIndex}`}>
                                            <td>
                                                    <span
                                                        className="admin-session-chip"
                                                        style={{
                                                            backgroundColor: row.sessionColor?.bg,
                                                            borderColor: row.sessionColor?.border,
                                                            color: row.sessionColor?.text
                                                        }}
                                                    >
                                                        {row.sessionLabel || '-'}
                                                    </span>
                                            </td>
                                            <td>
                                                {detailOpenDateKey ? formatDate(row[detailOpenDateKey]) : '-'}
                                            </td>
                                            <td>{row.spentTimeLabel || '-'}</td>
                                            <td>
                                                {detailPageKey
                                                    ? (row[detailPageKey]?.toString().replace(/^\/+/, '') || 'first-page')
                                                    : 'first-page'}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {detailError && <p className="admin-error">{detailError}</p>}

                        <div className="admin-pagination">
                            <button
                                className="admin-page-btn"
                                onClick={() => setDetailPage((prev) => Math.max(0, prev - 1))}
                                disabled={detailPage === 0 || detailLoading}
                            >
                                Geri
                            </button>

                            <div className="admin-page-numbers">
                                {visibleDetailPageNumbers.map((pageNumber) => (
                                    <button
                                        key={`detail-page-${pageNumber}`}
                                        className={`admin-page-btn ${pageNumber === detailPage ? 'active' : ''}`}
                                        onClick={() => setDetailPage(pageNumber)}
                                        disabled={detailLoading}
                                    >
                                        {pageNumber + 1}
                                    </button>
                                ))}
                            </div>

                            <button
                                className="admin-page-btn"
                                onClick={() => setDetailPage((prev) => Math.min(detailTotalPages - 1, prev + 1))}
                                disabled={detailLoading || detailTotalPages === 0 || detailPage >= detailTotalPages - 1}
                            >
                                İleri
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {simuDetailOpen && (
                <div className="admin-modal-backdrop" onClick={() => closeSimuDetailModal()}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <div>
                                <h3>Simülasyon Detayı</h3>
                                <p>
                                    Kullanıcı: {simuDetailUserInfo.fullname} | {simuDetailUserInfo.email}
                                </p>
                            </div>
                            <button type="button" className="admin-modal-close" onClick={() => closeSimuDetailModal()}>
                                Kapat
                            </button>
                        </div>

                        <div className="admin-meta admin-modal-meta">
                            <span>Toplam kayıt: {simuDetailTotalElements}</span>
                            <span>|</span>
                            <span>Aktif sayfa: {simuDetailTotalPages === 0 ? 0 : simuDetailPage + 1}</span>
                        </div>

                        <div className="admin-modal-content-area">
                            {simuDetailLoading && (
                                <div className="admin-modal-loading-overlay">
                                    <div className="admin-loading-spinner" />
                                    <p>Simülasyon detay yükleniyor...</p>
                                </div>
                            )}

                            <div className="admin-table-wrapper admin-modal-table-wrapper">
                                <table className="admin-table admin-modal-table">
                                    <thead>
                                    <tr>
                                        {simuDetailColumns.length === 0 && <th>Bilgi</th>}
                                        {orderedSimuDetailColumns.map((column) => (
                                            <th key={column}>
                                                {column === simuStartClientIdKey
                                                    ? 'Oturum Sayısı'
                                                    : column === simuEndClientIdKey
                                                        ? 'Tek Oturumda Tamamlandı Mı?'
                                                        : column === simuStartDateKey
                                                            ? 'Simülasyon Başlatma Tarihi'
                                                            : column === simuEndDateKey
                                                                ? 'Simülasyon Tamamlama Tarihi'
                                                                : column === simuUsernameKey
                                                                    ? 'Durum'
                                                        : column}
                                            </th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {!simuDetailLoading && simuDetailRowsProcessed.length === 0 && (
                                        <tr>
                                            <td colSpan={simuDetailColumns.length || 1} className="admin-empty">
                                                Simülasyon detay kaydı bulunamadı.
                                            </td>
                                        </tr>
                                    )}

                                    {simuDetailRowsProcessed.map((row, rowIndex) => (
                                        <tr key={`simu-detail-${rowIndex}`}>
                                            {orderedSimuDetailColumns.map((column) => (
                                                <td key={`simu-detail-${rowIndex}-${column}`}>
                                                    {column === simuStartClientIdKey
                                                        ? (row.sessionLabel || '-')
                                                        : column === simuStartDateKey || column === simuEndDateKey
                                                            ? formatDateAsUtcPlus3(row[column])
                                                        : column === simuUsernameKey
                                                            ? (
                                                                <span
                                                                    className={`admin-status-chip ${
                                                                        simuEndDateKey && row[simuEndDateKey] ? 'ok' : 'no'
                                                                    }`}
                                                                >
                                                                    {simuEndDateKey && row[simuEndDateKey] ? 'Tamamlandı' : 'Tamamlanmadı'}
                                                                </span>
                                                            )
                                                        : column === simuEndClientIdKey
                                                            ? (() => {
                                                                const endDate = simuEndDateKey ? row[simuEndDateKey] : null;
                                                                if (!endDate) return '-';

                                                                const startClientId = simuStartClientIdKey ? row[simuStartClientIdKey] : null;
                                                                const endClientId = row[simuEndClientIdKey];
                                                                if (!startClientId || !endClientId) return '-';

                                                                const sameSession = startClientId.toString().trim().toLowerCase() ===
                                                                    endClientId.toString().trim().toLowerCase();
                                                                return sameSession
                                                                    ? 'Aynı Oturumda Simülasyon Tamamlandı'
                                                                    : 'Farklı Oturumda Simülasyon Tamamlandı';
                                                            })()
                                                        : formatCellValue(column, row[column])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {simuDetailError && <p className="admin-error">{simuDetailError}</p>}

                        <div className="admin-pagination">
                            <button
                                className="admin-page-btn"
                                onClick={() => setSimuDetailPage((prev) => Math.max(0, prev - 1))}
                                disabled={simuDetailPage === 0 || simuDetailLoading}
                            >
                                Geri
                            </button>

                            <div className="admin-page-numbers">
                                {visibleSimuDetailPageNumbers.map((pageNumber) => (
                                    <button
                                        key={`simu-detail-page-${pageNumber}`}
                                        className={`admin-page-btn ${pageNumber === simuDetailPage ? 'active' : ''}`}
                                        onClick={() => setSimuDetailPage(pageNumber)}
                                        disabled={simuDetailLoading}
                                    >
                                        {pageNumber + 1}
                                    </button>
                                ))}
                            </div>

                            <button
                                className="admin-page-btn"
                                onClick={() => setSimuDetailPage((prev) => Math.min(simuDetailTotalPages - 1, prev + 1))}
                                disabled={simuDetailLoading || simuDetailTotalPages === 0 || simuDetailPage >= simuDetailTotalPages - 1}
                            >
                                İleri
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPage;
